import React, {
    forwardRef,
    useCallback,
    useEffect,
    useImperativeHandle,
    useLayoutEffect,
    useMemo,
    useRef,
} from 'react'
import { hostedNativeMediaEnabled } from 'lib/hostedMediaPlayback'
import HostedAudioPlayerCore from './HostedAudioPlayerCore'
import {
    claimMediaPlayback,
    createMediaPlaybackOwnerId,
    getMediaPlaybackState,
    isMediaKeyPlayingInDock,
    isMediaElementAtEnd,
    playMediaPlaybackOwner,
    persistMediaElementPlaybackState,
    releaseMediaPlayback,
    safeMediaCurrentTime,
    subscribeToMediaPlaybackState,
    syncMediaPlaybackOwner,
    transferMediaPlaybackControlFromDock,
    unregisterMediaPlaybackOwner,
} from 'state/mediaPlaybackCoordinator'

const AUDIO_END_THRESHOLD = 0.05

const clampAudioTime = (time, duration) => {
    if (!Number.isFinite(time) || time < 0) return 0
    if (!Number.isFinite(duration) || duration <= 0) return time
    return Math.min(time, duration)
}

const safeAudioDuration = (audio, fallback = 0) =>
    Number.isFinite(audio?.duration) && audio.duration > 0 ? audio.duration : fallback

const isAudioAtEnd = (audio, fallbackDuration = 0) =>
    isMediaElementAtEnd(audio, {
        fallbackDuration,
        endThreshold: AUDIO_END_THRESHOLD,
    })

/**
 * @typedef {Object} AudioPlayerCoreProps
 * @property {string | null | undefined} [mediaKey]
 * @property {string | null | undefined} [src]
 * @property {'audio' | 'video' | undefined} [as] The element to render: a radio
 *   station card plays a video song in place, with its picture.
 * @property {(presentation: { isHostedHere: boolean, chromeLayer: Element | null }) => void} [onHostedPresentation]
 *   Hosted video only: whether the dock's element is parked over originRef,
 *   and the layer to draw controls in above it.
 * @property {string | undefined} [className]
 * @property {React.CSSProperties | undefined} [style]
 * @property {string | undefined} [preload]
 * @property {boolean | undefined} [controls]
 * @property {boolean | undefined} [autoPlay]
 * @property {boolean | undefined} [isPaused]
 * @property {boolean | undefined} [persistPlayback]
 * @property {React.RefObject<Element>} [originRef]
 * @property {string | number | null | undefined} [resumePlaybackToken]
 * @property {number | undefined} [volume]
 * @property {number | undefined} [playbackRate]
 * @property {(duration: number) => void} [onDurationChange]
 * @property {(currentTime: number, duration: number) => void} [onProgressChange]
 * @property {() => void} [onPlaybackError]
 * @property {(isPlaying: boolean) => void} [onPlaybackStateChange]
 * @property {(isPlaying: boolean) => void} [onManualPlaybackChange]
 * @property {(snapshot: { currentTime: number, duration: number, isPlaying: boolean }) => void} [onSnapshotChange]
 * @property {(event: React.SyntheticEvent<HTMLAudioElement>) => void} [onClick]
 * @property {(event: React.SyntheticEvent<HTMLAudioElement>) => void} [onMouseDown]
 * @property {(event: React.SyntheticEvent<HTMLAudioElement>) => void} [onPointerDown]
 * @property {(event: React.SyntheticEvent<HTMLAudioElement>) => void} [onTouchStart]
 */

/** @type {React.ForwardRefRenderFunction<any, AudioPlayerCoreProps>} */
const AudioPlayerCoreInner = (
    {
        mediaKey,
        src,
        as = 'audio',
        className = undefined,
        style = undefined,
        preload = 'metadata',
        controls = false,
        autoPlay = false,
        isPaused,
        persistPlayback = true,
        resumePlaybackToken,
        volume = 1,
        playbackRate = 1,
        onDurationChange,
        onProgressChange,
        onPlaybackError,
        onPlaybackStateChange,
        onManualPlaybackChange,
        onSnapshotChange,
        onClick,
        onMouseDown,
        onPointerDown,
        onTouchStart,
    },
    ref,
) => {
    const audioRef = useRef(null)
    const ownerIdRef = useRef(createMediaPlaybackOwnerId('audio-player'))
    const autoPlaySuppressedRef = useRef(false)
    const pauseRequestedByStoryRef = useRef(false)
    const manualPlaybackIntentRef = useRef(null)
    const reportedDurationRef = useRef(null)
    const handledResumeTokenRef = useRef(null)
    const pendingResumeRequestRef = useRef(false)
    const restoredOnMountRef = useRef(false)
    const lastDurationRef = useRef(0)
    const lastCurrentTimeRef = useRef(0)

    const effectiveMediaKey = useMemo(
        () => mediaKey || (src ? `audio-attachment:${String(src)}` : null),
        [mediaKey, src],
    )
    const shouldPersistPlayback = Boolean(persistPlayback && effectiveMediaKey)

    const emitSnapshot = useCallback(
        (audio, overrides = {}) => {
            const duration = overrides.duration ?? safeAudioDuration(audio, lastDurationRef.current)
            const currentTime =
                overrides.currentTime ??
                clampAudioTime(safeMediaCurrentTime(audio, lastCurrentTimeRef.current), duration)
            const isPlaying = overrides.isPlaying ?? Boolean(audio && !audio.paused && !audio.ended)

            lastDurationRef.current = duration
            lastCurrentTimeRef.current = currentTime
            onSnapshotChange?.({
                currentTime,
                duration,
                isPlaying,
            })

            if (
                duration > 0 &&
                (!Number.isFinite(reportedDurationRef.current) ||
                    Math.abs(reportedDurationRef.current - duration) > 0.01)
            ) {
                reportedDurationRef.current = duration
                onDurationChange?.(duration)
            }

            if (duration > 0) {
                onProgressChange?.(currentTime, duration)
            }
        },
        [onDurationChange, onProgressChange, onSnapshotChange],
    )

    const persistPlaybackState = useCallback(
        (overrides = {}, mediaEl = audioRef.current) => {
            if (!shouldPersistPlayback) return null

            return persistMediaElementPlaybackState(effectiveMediaKey, mediaEl, overrides, {
                fallbackDuration: lastDurationRef.current,
                endThreshold: AUDIO_END_THRESHOLD,
                skipAtEnd: overrides.currentTime == null,
            })
        },
        [effectiveMediaKey, shouldPersistPlayback],
    )

    const clearPlaybackStateAtStart = useCallback(() => {
        const audio = audioRef.current
        if (audio) {
            try {
                audio.currentTime = 0
            } catch {}
        }
        persistPlaybackState({ currentTime: 0 })
        emitSnapshot(audio, {
            currentTime: 0,
            isPlaying: false,
        })
    }, [emitSnapshot, persistPlaybackState])

    const restoreStoredCurrentTime = useCallback(
        audio => {
            if (!shouldPersistPlayback || !audio || !effectiveMediaKey) return false
            if (restoredOnMountRef.current) return false

            const stored = getMediaPlaybackState(effectiveMediaKey)
            if (!Number.isFinite(stored?.currentTime) || stored.currentTime <= 0.05) {
                restoredOnMountRef.current = true
                return false
            }

            const duration = safeAudioDuration(audio, lastDurationRef.current)
            if (duration > 0 && stored.currentTime >= duration - 0.25) {
                restoredOnMountRef.current = true
                clearPlaybackStateAtStart()
                return false
            }

            try {
                if (Math.abs((audio.currentTime || 0) - stored.currentTime) > 0.05) {
                    audio.currentTime = stored.currentTime
                }
                restoredOnMountRef.current = true
                emitSnapshot(audio, {
                    currentTime: stored.currentTime,
                    duration,
                })
                persistPlaybackState({ currentTime: stored.currentTime })
                return true
            } catch {
                restoredOnMountRef.current = true
                return false
            }
        },
        [
            clearPlaybackStateAtStart,
            effectiveMediaKey,
            emitSnapshot,
            persistPlaybackState,
            shouldPersistPlayback,
        ],
    )

    const syncFromAudio = useCallback(
        (audio, options = {}) => {
            if (!audio) return
            const { persistPlaybackState: shouldPersist = false } = options
            const duration = safeAudioDuration(audio, lastDurationRef.current)
            const currentTime = clampAudioTime(safeMediaCurrentTime(audio), duration)

            emitSnapshot(audio, {
                currentTime,
                duration,
            })

            if (shouldPersist && !isAudioAtEnd(audio, duration)) {
                persistPlaybackState({ currentTime })
            }
        },
        [emitSnapshot, persistPlaybackState],
    )

    const play = useCallback(async () => {
        const audio = audioRef.current
        if (!audio) return

        if (isAudioAtEnd(audio, lastDurationRef.current)) {
            clearPlaybackStateAtStart()
        }

        manualPlaybackIntentRef.current = 'play'
        autoPlaySuppressedRef.current = false
        if (!playMediaPlaybackOwner(ownerIdRef.current, { transferFromDock: true })) {
            await audio.play()
        }
    }, [clearPlaybackStateAtStart])

    const pause = useCallback(() => {
        const audio = audioRef.current
        if (!audio || audio.paused) return

        manualPlaybackIntentRef.current = 'pause'
        autoPlaySuppressedRef.current = true
        audio.pause()
    }, [])

    const togglePlayback = useCallback(async () => {
        const audio = audioRef.current
        if (!audio) return

        if (audio.paused || audio.ended) {
            await play()
        } else {
            pause()
        }
    }, [pause, play])

    const seekTo = useCallback(
        nextTime => {
            const audio = audioRef.current
            if (!audio) return

            transferMediaPlaybackControlFromDock(ownerIdRef.current)
            const duration = safeAudioDuration(audio, lastDurationRef.current)
            const clampedTime = clampAudioTime(nextTime, duration)
            try {
                audio.currentTime = clampedTime
                emitSnapshot(audio, {
                    currentTime: clampedTime,
                    duration,
                })
                persistPlaybackState({ currentTime: clampedTime })
            } catch (error) {
                console.warn('Failed to seek audio attachment:', error)
            }
        },
        [emitSnapshot, persistPlaybackState],
    )

    useImperativeHandle(
        ref,
        () => ({
            getAudioElement: () => audioRef.current,
            claimControl: () => transferMediaPlaybackControlFromDock(ownerIdRef.current),
            pause,
            play,
            seekTo,
            togglePlayback,
        }),
        [pause, play, seekTo, togglePlayback],
    )

    useEffect(() => {
        syncMediaPlaybackOwner(ownerIdRef.current, effectiveMediaKey, audioRef.current)
    }, [effectiveMediaKey, src])

    useEffect(() => {
        return () => {
            unregisterMediaPlaybackOwner(ownerIdRef.current)
        }
    }, [])

    useEffect(() => {
        autoPlaySuppressedRef.current = false
        pauseRequestedByStoryRef.current = false
        manualPlaybackIntentRef.current = null
        reportedDurationRef.current = null
        handledResumeTokenRef.current = null
        pendingResumeRequestRef.current = false
        restoredOnMountRef.current = false
        lastDurationRef.current = 0
        lastCurrentTimeRef.current = 0
        emitSnapshot(audioRef.current, {
            currentTime: 0,
            duration: 0,
            isPlaying: false,
        })
    }, [autoPlay, effectiveMediaKey, emitSnapshot, src])

    useEffect(() => {
        if (!shouldPersistPlayback || !effectiveMediaKey) return undefined

        return subscribeToMediaPlaybackState(effectiveMediaKey, nextState => {
            const audio = audioRef.current
            if (!audio || !audio.paused || audio.ended) return
            if (isMediaKeyPlayingInDock(effectiveMediaKey)) return
            if (!Number.isFinite(nextState?.currentTime) || nextState.currentTime <= 0.05) return

            const duration = safeAudioDuration(audio, lastDurationRef.current)
            if (duration > 0 && nextState.currentTime >= duration - 0.25) return

            const nextTime = clampAudioTime(nextState.currentTime, duration)
            try {
                if (Math.abs((audio.currentTime || 0) - nextTime) > 0.05) {
                    audio.currentTime = nextTime
                }
                emitSnapshot(audio, {
                    currentTime: nextTime,
                    duration,
                    isPlaying: false,
                })
            } catch {}
        })
    }, [effectiveMediaKey, emitSnapshot, shouldPersistPlayback])

    useEffect(() => {
        if (!shouldPersistPlayback || !effectiveMediaKey) return undefined
        const audio = audioRef.current
        if (!audio) return undefined

        const syncPlaybackState = options => {
            if (options?.skipAtEnd && isAudioAtEnd(audio, lastDurationRef.current)) return
            persistPlaybackState({}, audio)
        }
        const handleTimeUpdate = () => syncPlaybackState()
        const handleSeeked = () => syncPlaybackState()
        const handlePause = () => syncPlaybackState({ skipAtEnd: true })
        const doc = typeof document !== 'undefined' ? document : null
        const handleVisibilityChange = () => {
            if (doc?.hidden) syncPlaybackState()
        }
        const handlePageHide = () => syncPlaybackState()

        audio.addEventListener('timeupdate', handleTimeUpdate)
        audio.addEventListener('seeked', handleSeeked)
        audio.addEventListener('pause', handlePause)
        doc?.addEventListener('visibilitychange', handleVisibilityChange)
        window.addEventListener('pagehide', handlePageHide)

        return () => {
            syncPlaybackState()
            audio.removeEventListener('timeupdate', handleTimeUpdate)
            audio.removeEventListener('seeked', handleSeeked)
            audio.removeEventListener('pause', handlePause)
            doc?.removeEventListener('visibilitychange', handleVisibilityChange)
            window.removeEventListener('pagehide', handlePageHide)
        }
    }, [effectiveMediaKey, persistPlaybackState, shouldPersistPlayback])

    useLayoutEffect(() => {
        if (!shouldPersistPlayback || !effectiveMediaKey || restoredOnMountRef.current) {
            return undefined
        }

        const audio = audioRef.current
        if (!audio) return undefined

        const applyPlaybackState = () => {
            restoreStoredCurrentTime(audio)
        }

        if (audio.readyState >= 1) {
            applyPlaybackState()
            return undefined
        }

        audio.addEventListener('loadedmetadata', applyPlaybackState, { once: true })
        return () => audio.removeEventListener('loadedmetadata', applyPlaybackState)
    }, [effectiveMediaKey, restoreStoredCurrentTime, shouldPersistPlayback])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        audio.playbackRate = playbackRate
    }, [playbackRate])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        audio.volume = Math.max(0, Math.min(1, Number(volume)))
    }, [src, volume])

    useEffect(() => {
        if (resumePlaybackToken == null) return
        if (handledResumeTokenRef.current === resumePlaybackToken) return

        handledResumeTokenRef.current = resumePlaybackToken
        autoPlaySuppressedRef.current = false
        pendingResumeRequestRef.current = true

        const audio = audioRef.current
        if (!audio || !audio.paused) return

        if (isAudioAtEnd(audio, lastDurationRef.current)) {
            clearPlaybackStateAtStart()
        }

        audio.play().catch(error => {
            pendingResumeRequestRef.current = false
            if (error?.name === 'AbortError') return
            onPlaybackError?.()
            console.warn('Failed to resume audio attachment:', error)
        })
    }, [clearPlaybackStateAtStart, onPlaybackError, resumePlaybackToken])

    useEffect(() => {
        const audio = audioRef.current
        if (!audio) return

        if (isPaused) {
            if (pendingResumeRequestRef.current) return
            if (!audio.paused) {
                pauseRequestedByStoryRef.current = true
                audio.pause()
            }
            return
        }

        if (!autoPlay || autoPlaySuppressedRef.current || !audio.paused) return

        if (isAudioAtEnd(audio, lastDurationRef.current)) {
            clearPlaybackStateAtStart()
        }

        audio.play().catch(error => {
            if (error?.name === 'AbortError') return
            onPlaybackError?.()
            console.warn('Failed to autoplay audio attachment:', error)
        })
    }, [autoPlay, clearPlaybackStateAtStart, isPaused, onPlaybackError, src])

    const handleLoadedMetadata = useCallback(
        event => {
            restoreStoredCurrentTime(event.currentTarget)
            syncFromAudio(event.currentTarget)
        },
        [restoreStoredCurrentTime, syncFromAudio],
    )

    const handleDurationChange = useCallback(
        event => {
            syncFromAudio(event.currentTarget)
        },
        [syncFromAudio],
    )

    const handleTimeUpdate = useCallback(
        event => {
            syncFromAudio(event.currentTarget, {
                persistPlaybackState: false,
            })
        },
        [syncFromAudio],
    )

    const handlePlay = useCallback(
        event => {
            pendingResumeRequestRef.current = false
            autoPlaySuppressedRef.current = false
            pauseRequestedByStoryRef.current = false
            const manualPlaybackIntent = manualPlaybackIntentRef.current
            onPlaybackStateChange?.(true)
            if (manualPlaybackIntent === 'play') {
                onManualPlaybackChange?.(true)
            }
            manualPlaybackIntentRef.current = null
            syncMediaPlaybackOwner(ownerIdRef.current, effectiveMediaKey, event.currentTarget)
            claimMediaPlayback(ownerIdRef.current, {
                transferFromDock: manualPlaybackIntent === 'play',
            })
            syncFromAudio(event.currentTarget, {
                persistPlaybackState: true,
            })
            emitSnapshot(event.currentTarget, { isPlaying: true })
        },
        [
            effectiveMediaKey,
            emitSnapshot,
            onManualPlaybackChange,
            onPlaybackStateChange,
            syncFromAudio,
        ],
    )

    const handlePause = useCallback(
        event => {
            pendingResumeRequestRef.current = false
            const pauseAtEnd = isAudioAtEnd(event.currentTarget, lastDurationRef.current)
            if (!pauseAtEnd) {
                onPlaybackStateChange?.(false)
            }
            const manualPlaybackIntent = manualPlaybackIntentRef.current
            manualPlaybackIntentRef.current = null
            const pauseRequestedByStory = pauseRequestedByStoryRef.current
            pauseRequestedByStoryRef.current = false
            if (!pauseRequestedByStory && !pauseAtEnd) {
                autoPlaySuppressedRef.current = true
            }
            if (manualPlaybackIntent === 'pause') {
                onManualPlaybackChange?.(false)
            }
            releaseMediaPlayback(ownerIdRef.current)
            syncFromAudio(event.currentTarget, {
                persistPlaybackState: !pauseAtEnd,
            })
            emitSnapshot(event.currentTarget, { isPlaying: false })
        },
        [emitSnapshot, onManualPlaybackChange, onPlaybackStateChange, syncFromAudio],
    )

    const handleEnded = useCallback(
        event => {
            pendingResumeRequestRef.current = false
            autoPlaySuppressedRef.current = false
            pauseRequestedByStoryRef.current = false
            manualPlaybackIntentRef.current = null
            onPlaybackStateChange?.(false)
            const endedDuration = safeAudioDuration(event.currentTarget, lastDurationRef.current)
            releaseMediaPlayback(ownerIdRef.current, {
                ended: true,
                mediaKey: effectiveMediaKey,
            })
            emitSnapshot(event.currentTarget, {
                currentTime: endedDuration > 0 ? endedDuration : 0,
                duration: endedDuration,
                isPlaying: false,
            })
            persistPlaybackState({ currentTime: 0 })
        },
        [effectiveMediaKey, emitSnapshot, onPlaybackStateChange, persistPlaybackState],
    )

    const handleError = useCallback(
        event => {
            pendingResumeRequestRef.current = false
            onPlaybackStateChange?.(false)
            manualPlaybackIntentRef.current = null
            releaseMediaPlayback(ownerIdRef.current)

            const audio = event.currentTarget
            const errorTime =
                Number.isFinite(audio?.currentTime) && audio.currentTime > 0.05
                    ? audio.currentTime
                    : 0
            persistPlaybackState({ currentTime: errorTime })
            emitSnapshot(audio, {
                currentTime: errorTime,
                isPlaying: false,
            })
            onPlaybackError?.()
        },
        [emitSnapshot, onPlaybackError, onPlaybackStateChange, persistPlaybackState],
    )

    const MediaElement = as === 'video' ? 'video' : 'audio'
    return (
        <MediaElement
            ref={audioRef}
            playsInline={as === 'video' ? true : undefined}
            className={className}
            style={style}
            src={src}
            preload={preload}
            controls={controls}
            data-no-modal-interaction
            data-post-embed-navigation-exempt="true"
            onClick={onClick}
            onMouseDown={onMouseDown}
            onPointerDown={onPointerDown}
            onTouchStart={onTouchStart}
            onLoadedMetadata={handleLoadedMetadata}
            onDurationChange={handleDurationChange}
            onTimeUpdate={handleTimeUpdate}
            onPlay={handlePlay}
            onPause={handlePause}
            onEnded={handleEnded}
            onError={handleError}
        />
    )
}

const ElementAudioPlayerCore = forwardRef(AudioPlayerCoreInner)

/**
 * Hosted playback replaces the whole element-backed core rather than threading
 * conditionals through it: with the feature on, the media dock owns the single
 * `<audio>` for the track and this surface only drives it. The decision is
 * stable for the session, so the two implementations never swap under React.
 */
/** @type {React.ForwardRefRenderFunction<any, AudioPlayerCoreProps>} */
// A video song hosts visually: the dock's own element is parked over the
// card's stage (originRef), so leaving the card never reloads the picture.
const AudioPlayerCoreRouter = (props, ref) =>
    hostedNativeMediaEnabled() && props.persistPlayback !== false ? (
        <HostedAudioPlayerCore
            {...props}
            placement={props.as === 'video' ? 'visual' : 'silent'}
            ref={ref}
        />
    ) : (
        <ElementAudioPlayerCore {...props} ref={ref} />
    )

const AudioPlayerCore = forwardRef(AudioPlayerCoreRouter)

export default React.memo(AudioPlayerCore)
